import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";

interface StatCardProps {
  icon: ReactNode;
  count: number;
  label: string;
  /** Tailwind bg/text classes for the icon tile. Ignored when `muted`. */
  iconBg?: string;
  iconColor?: string;
  /** Dim the icon + number for a resting/zero state (e.g. "All clear"). */
  muted?: boolean;
  /** Optional footer slot (a CTA line, a status). Use `mt-auto` to pin it. */
  footer?: ReactNode;
  className?: string;
}

/**
 * The shared metric card used across the dashboard's attention row and the
 * adjudication queue summary: an icon tile + a big number on one line, a label
 * beneath, and an optional footer slot.
 */
export function StatCard({
  icon,
  count,
  label,
  iconBg,
  iconColor,
  muted = false,
  footer,
  className,
}: StatCardProps) {
  return (
    <Card size="sm" className={cn("h-full rounded-xl", className)}>
      <CardContent className="flex flex-1 flex-col items-start gap-2.5">
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "flex size-9 shrink-0 items-center justify-center rounded-lg",
              muted ? "bg-muted text-text-muted" : cn(iconBg, iconColor),
            )}
          >
            {icon}
          </span>
          <span
            className={cn(
              "text-2xl font-bold leading-none tabular-nums poppins",
              muted ? "text-text-muted" : "text-text-primary",
            )}
          >
            {count}
          </span>
        </div>
        <span className="text-xs text-text-muted mulish leading-snug">
          {label}
        </span>
        {footer}
      </CardContent>
    </Card>
  );
}
